import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import { useLang } from "../lib/i18n";

export default function Login() {
    const { login, setSession } = useAuth();
    const { T } = useLang();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/app";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [restoreInfo, setRestoreInfo] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate(next, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "deletion_pending") {
                setRestoreInfo({
                    message: detail.message,
                    purgeAfter: detail.purge_after_at,
                });
            } else {
                setError(
                    typeof detail === "string"
                        ? detail
                        : "Could not sign in. Check your credentials."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const restore = async () => {
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/auth/account/restore", { email, password });
            setSession(data);
            toast.success("Your account has been restored. Welcome back!");
            navigate("/app", { replace: true });
        } catch {
            setError("Could not restore the account. Check your credentials and try again.");
            setRestoreInfo(null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F0E8]">
            <section className="hidden lg:flex flex-col justify-between p-14 bg-[#0D1B3E] text-[#F5F0E8]">
                <Link to="/" className="flex items-center gap-3" data-testid="login-brand">
                    <EuStars size={36} />
                    <span className="font-display text-xl">EuroLaw Hub</span>
                </Link>
                <div>
                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                        {T("Welcome back, Counsel", "Tekrar hoş geldiniz")}
                    </div>
                    <h1 className="font-display text-4xl mt-3 leading-[1.1] tracking-tight">
                        {T("The full corpus of EU law,", "AB hukukunun tüm külliyatı,")}
                        <br />
                        {T("ready when you are.", "siz hazır olduğunuzda hazır.")}
                    </h1>
                    <p className="mt-5 text-[#F5F0E8]/70 max-w-md">
                        {T("Sign in to continue your research, review your alerts, and access your saved conversations and documents.",
                            "Araştırmanıza devam etmek, uyarılarınızı görmek ve kayıtlı sohbetlerinize ve belgelerinize erişmek için giriş yapın.")}
                    </p>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#F5F0E8]/50">
                    GDPR · AI Act · DORA · NIS2 · CJEU
                </div>
            </section>

            <section className="flex items-center justify-center p-8 lg:p-14">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md"
                    data-testid="login-form"
                >
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <EuStars size={28} />
                        <span className="font-display text-lg">EuroLaw Hub</span>
                    </div>
                    <div className="label-eyebrow">{T("Sign in", "Giriş yap")}</div>
                    <h2 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                        {T("Welcome back.", "Tekrar hoş geldiniz.")}
                    </h2>
                    <p className="mt-3 text-sm text-[#0D1B3E]/70">
                        {T("New here?", "Yeni misiniz?")}{" "}
                        <Link to="/signup" className="underline underline-offset-4 text-[#0D1B3E] hover:text-[#C9A84C]" data-testid="login-link-signup">
                            {T("Create an account", "Hesap oluşturun")}
                        </Link>
                    </p>

                    <div className="mt-8 space-y-4">
                        <Field
                            label={T("Email", "E-posta")}
                            type="email"
                            value={email}
                            onChange={setEmail}
                            testId="login-input-email"
                            autoComplete="email"
                            required
                        />
                        <PasswordField
                            label={T("Password", "Şifre")}
                            value={password}
                            onChange={setPassword}
                            testId="login-input-password"
                            autoComplete="current-password"
                        />
                    </div>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="mt-4 text-sm border border-red-500/40 bg-red-50 text-red-900 px-3 py-2"
                        >
                            {error}
                        </div>
                    )}

                    {restoreInfo && (
                        <div
                            data-testid="login-restore-panel"
                            className="mt-4 border border-[#C9A84C]/60 bg-[#C9A84C]/10 px-4 py-4"
                        >
                            <div className="text-sm font-medium text-[#0D1B3E]">
                                This account is scheduled for permanent deletion
                                {restoreInfo.purgeAfter
                                    ? ` on ${new Date(restoreInfo.purgeAfter).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`
                                    : ""}.
                            </div>
                            <p className="mt-2 text-sm text-[#0D1B3E]/75">
                                You can restore it now. Your saved work, documents and
                                conversations are still intact.
                            </p>
                            <button
                                type="button"
                                onClick={restore}
                                disabled={submitting}
                                className="btn-primary mt-4 w-full disabled:opacity-60"
                                data-testid="login-restore-button"
                            >
                                {submitting ? "Restoring…" : "Restore my account"}
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary mt-7 w-full disabled:opacity-60"
                        data-testid="login-submit-button"
                    >
                        {submitting ? T("Signing in…", "Giriş yapılıyor…") : T("Sign in", "Giriş yap")}
                    </button>

                    <div className="mt-4 flex items-center justify-between text-sm">
                        <Link
                            to="/forgot-password"
                            className="text-[#0D1B3E]/70 hover:text-[#C9A84C] underline"
                            data-testid="login-forgot-password-link"
                        >
                            {T("Forgot password?", "Şifrenizi mi unuttunuz?")}
                        </Link>
                        <Link
                            to="/signup"
                            className="text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                            data-testid="login-signup-link"
                        >
                            {T("Create an account →", "Hesap oluşturun →")}
                        </Link>
                    </div>
                </form>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId, ...rest }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="field"
                data-testid={testId}
                {...rest}
            />
        </label>
    );
}

function PasswordField({ label, value, onChange, testId, ...rest }) {
    const [show, setShow] = useState(false);
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                {label}
            </span>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="field pr-11"
                    data-testid={testId}
                    required
                    {...rest}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50 hover:text-[#0D1B3E]"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid={`${testId}-toggle`}
                >
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
            </div>
        </label>
    );
}
